const PEEK_PERCENT = 22;
const DRAG_THRESHOLD = 8;
const HORIZONTAL_THRESHOLD = 12;
const PEEK_HINT_DURATION = 500;
const PEEK_HINT_HOLD_DURATION = 2000;
const MOBILE_MEDIA_QUERY = '(max-width: 749px)';

const getParentCollectionSlider = (el) =>
  el?.closest('[id^="Slider-"], .slider.slider--tablet, .slider.slider--desktop, .slider--tablet');

const prefersReducedMotion = () =>
  window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const isMobileViewport = () =>
  window.matchMedia && window.matchMedia(MOBILE_MEDIA_QUERY).matches;

class CardImageSlider {
  constructor(root) {
    this.root = root;
    this.track = root.querySelector('[data-card-image-slider-track]');
    this.slides = [...root.querySelectorAll('[data-card-image-slider-slide]')];
    this.index = 0;
    this.slideWidth = 0;
    this.dragOffset = 0;
    this.peekPercent = 0;
    this.isDragging = false;
    this.didDrag = false;
    this.pointerId = null;
    this.startX = 0;
    this.startY = 0;
    this.startTranslate = 0;
    this.startScrollX = 0;
    this.startScrollY = 0;
    this.pointerType = null;
    this.gestureAxis = null;
    this.parentLocks = null;
    this.peekHintPlayed = false;
    this.resizeObserver = null;

    if (!this.track || this.slides.length < 2) return;

    this.root.dataset.cardImageSliderInit = 'true';
    this.setPeekPercent(0, false);
    this.bindEvents();
    this.measure();
    this.setTranslate(this.getBaseTranslate(), false);
    this.syncFirstImageBadges();
    this.schedulePeekHint();
  }

  getPeekPx() {
    const width = this.root.getBoundingClientRect().width;
    return width * (this.peekPercent / 100);
  }

  get maxIndex() {
    return this.slides.length - 1;
  }

  getBaseTranslate() {
    return -(this.index * this.slideWidth);
  }

  measure() {
    const slide = this.slides[0];
    if (slide) {
      this.slideWidth = slide.getBoundingClientRect().width;
      return;
    }

    const width = this.root.getBoundingClientRect().width;
    this.slideWidth = Math.max(width - this.getPeekPx(), 0);
  }

  setTranslate(baseX, animate) {
    const track = this.track;
    if (!track) return;
    track.classList.toggle('is-snapping', animate);
    const x = Math.round(baseX + this.dragOffset);
    track.style.transform = `translate3d(${x}px, 0, 0)`;
  }

  setPeekPercent(percent, animate = false) {
    const nextPercent = Math.max(0, Math.min(percent, PEEK_PERCENT));
    this.peekPercent = nextPercent;
    this.root.style.setProperty('--card-image-slider-peek', `${nextPercent}%`);

    if (animate) {
      this.slides.forEach((slide) => {
        slide.offsetWidth;
      });
    }

    this.measure();
    this.setTranslate(this.getBaseTranslate(), animate);
  }

  clampIndex(index) {
    return Math.max(0, Math.min(index, this.maxIndex));
  }

  snapToIndex(index, animate = true) {
    this.index = this.clampIndex(index);
    this.dragOffset = 0;
    this.setPeekPercent(0, animate);
    this.syncFirstImageBadges();
  }

  syncFirstImageBadges() {
    const media = this.root.closest('.card__media');
    if (!media) return;
    const draggedOffFirst = this.index === 0 && this.dragOffset < -(this.slideWidth || 0) * 0.2;
    media.classList.toggle('card__media--not-first-image', this.index > 0 || draggedOffFirst);
  }

  lockParentCarousel() {
    const parentSlider = getParentCollectionSlider(this.root);
    const locks = { parentSlider, parentScrollLeft: null };
    if (parentSlider) {
      locks.parentScrollLeft = parentSlider.scrollLeft;
    }
    return locks;
  }

  restoreParentCarousel() {
    if (!this.parentLocks?.parentSlider || this.parentLocks.parentScrollLeft == null) return;
    this.parentLocks.parentSlider.scrollLeft = this.parentLocks.parentScrollLeft;
  }

  pinWindowScroll() {
    if (window.scrollX === this.startScrollX && window.scrollY === this.startScrollY) return;
    window.scrollTo(this.startScrollX, this.startScrollY);
  }

  resolveGestureAxis(dx, dy) {
    if (this.gestureAxis) return this.gestureAxis;

    const absX = Math.abs(dx);
    const absY = Math.abs(dy);

    if (absX < DRAG_THRESHOLD && absY < DRAG_THRESHOLD) return null;

    // Ties and mostly-vertical moves belong to the page, not the gallery.
    if (absY >= absX) {
      this.gestureAxis = 'y';
      return this.gestureAxis;
    }

    if (absX >= HORIZONTAL_THRESHOLD) {
      this.gestureAxis = 'x';
      return this.gestureAxis;
    }

    return null;
  }

  capturePointer(pointerId) {
    // setPointerCapture on iOS Safari can scroll the page a few pixels.
    if (this.pointerType === 'touch' || !this.root.setPointerCapture) return;
    try {
      this.root.setPointerCapture(pointerId);
    } catch (_) {
      /* ignore */
    }
  }

  releasePointer(pointerId) {
    if (this.pointerType === 'touch' || !this.root.releasePointerCapture) return;
    try {
      this.root.releasePointerCapture(pointerId);
    } catch (_) {
      /* ignore */
    }
  }

  bindEvents() {
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerEnd = this.onPointerEnd.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onDragStart = this.onDragStart.bind(this);
    this.onResize = this.onResize.bind(this);
    this.dragListenersBound = false;

    this.root.addEventListener('pointerdown', this.onPointerDown);
    // iOS only cancels scrolling from a non-passive touchmove that exists
    // before the gesture starts. pointermove preventDefault is ignored.
    this.root.addEventListener('touchmove', this.onTouchMove, { passive: false });
    this.root.addEventListener('click', this.onClick, true);
    this.root.addEventListener('dragstart', this.onDragStart);

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this.root);
    } else {
      window.addEventListener('resize', this.onResize);
    }
  }

  bindDragListeners() {
    if (this.dragListenersBound) return;
    this.dragListenersBound = true;
    window.addEventListener('pointermove', this.onPointerMove, { passive: true });
    window.addEventListener('pointerup', this.onPointerEnd);
    window.addEventListener('pointercancel', this.onPointerEnd);
  }

  unbindDragListeners() {
    if (!this.dragListenersBound) return;
    this.dragListenersBound = false;
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerEnd);
    window.removeEventListener('pointercancel', this.onPointerEnd);
  }

  onResize() {
    this.measure();
    this.setTranslate(this.getBaseTranslate(), false);
  }

  onPointerDown(e) {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (e.target.closest('.swatch-input__input, .swatch-input__label, .swatches-remaining, .swatches__wrapper')) {
      return;
    }

    this.cancelPeekHint();
    this.pointerId = e.pointerId;
    this.pointerType = e.pointerType;
    this.gestureAxis = null;
    this.startX = e.clientX;
    this.startY = e.clientY;
    this.startScrollX = window.scrollX;
    this.startScrollY = window.scrollY;
    this.startTranslate = this.getBaseTranslate() + this.dragOffset;
    this.isDragging = false;
    this.didDrag = false;

    this.bindDragListeners();
  }

  onDragStart(e) {
    e.preventDefault();
  }

  onTouchMove(e) {
    if (this.pointerId == null || this.gestureAxis === 'y') return;

    const touch = e.touches[0];
    if (!touch) return;

    const axis = this.resolveGestureAxis(touch.clientX - this.startX, touch.clientY - this.startY);
    if ((axis === 'x' || this.isDragging) && e.cancelable) {
      e.preventDefault();
    }
  }

  onPointerMove(e) {
    if (this.pointerId !== e.pointerId || this.gestureAxis === 'y') return;

    const dx = e.clientX - this.startX;
    const dy = e.clientY - this.startY;
    const axis = this.resolveGestureAxis(dx, dy);

    if (axis === 'y') {
      this.root.classList.remove('is-dragging');
      this.parentLocks = null;
      this.isDragging = false;
      this.releasePointer(e.pointerId);
      return;
    }

    if (axis !== 'x') return;

    if (!this.isDragging) {
      this.isDragging = true;
      this.didDrag = true;
      this.setPeekPercent(0, false);
      this.parentLocks = this.lockParentCarousel();
      this.root.classList.add('is-dragging');
      this.track.classList.remove('is-snapping');
      this.pinWindowScroll();
      this.capturePointer(e.pointerId);
    }

    e.stopPropagation();

    let nextOffset = dx;
    const minTranslate = -(this.maxIndex * this.slideWidth);
    const maxTranslate = 0;
    const proposed = this.startTranslate + nextOffset;

    if (proposed > maxTranslate) {
      nextOffset = maxTranslate - this.startTranslate + (proposed - maxTranslate) * 0.25;
    } else if (proposed < minTranslate) {
      nextOffset = minTranslate - this.startTranslate + (proposed - minTranslate) * 0.25;
    }

    this.dragOffset = nextOffset;
    this.setTranslate(this.getBaseTranslate(), false);
    this.syncFirstImageBadges();
    this.restoreParentCarousel();
  }

  onPointerEnd(e) {
    if (this.pointerId !== e.pointerId) return;

    if (this.isDragging && this.gestureAxis === 'x') {
      const moved = this.dragOffset;
      const threshold = this.slideWidth * 0.2;
      let nextIndex = this.index;

      if (moved < -threshold) {
        nextIndex += 1;
      } else if (moved > threshold) {
        nextIndex -= 1;
      }

      this.root.classList.remove('is-dragging');
      this.parentLocks = null;
      this.snapToIndex(nextIndex, true);
    }

    this.releasePointer(e.pointerId);
    this.pointerId = null;
    this.pointerType = null;
    this.gestureAxis = null;
    this.isDragging = false;
    this.unbindDragListeners();
  }

  onClick(e) {
    if (!this.didDrag) return;
    e.preventDefault();
    e.stopPropagation();
    this.didDrag = false;
  }

  cancelPeekHint() {
    if (this.peekHintFrame) {
      cancelAnimationFrame(this.peekHintFrame);
      this.peekHintFrame = null;
    }
    this.root.classList.remove('is-peek-hinting');
    this.setPeekPercent(0, false);
  }

  schedulePeekHint() {
    if (this.peekHintPlayed || prefersReducedMotion()) return;

    const card = this.root.closest('.card-wrapper');
    const cardIndex = parseInt(card?.dataset?.cardIndex, 10);
    if (cardIndex !== 1) return;

    this.peekHintTimer = window.setTimeout(() => {
      this.playPeekHint();
    }, 400);
  }

  playPeekHint() {
    if (this.peekHintPlayed || this.isDragging || prefersReducedMotion()) return;
    if (this.index >= this.maxIndex) return;

    this.peekHintPlayed = true;
    this.root.classList.add('is-peek-hinting');
    const start = performance.now();
    const riseEnd = PEEK_HINT_DURATION;
    const holdEnd = riseEnd + PEEK_HINT_HOLD_DURATION;
    const fallEnd = holdEnd + PEEK_HINT_DURATION;

    const tick = (now) => {
      if (this.isDragging) return;

      const elapsed = now - start;

      if (elapsed < riseEnd) {
        const progress = elapsed / PEEK_HINT_DURATION;
        const eased = Math.sin(progress * Math.PI * 0.5);
        this.setPeekPercent(PEEK_PERCENT * eased, false);
      } else if (elapsed < holdEnd) {
        this.setPeekPercent(PEEK_PERCENT, false);
      } else if (elapsed < fallEnd) {
        const progress = (elapsed - holdEnd) / PEEK_HINT_DURATION;
        const eased = Math.cos(progress * Math.PI * 0.5);
        this.setPeekPercent(PEEK_PERCENT * eased, false);
      } else {
        this.root.classList.remove('is-peek-hinting');
        this.setPeekPercent(0, true);
        this.peekHintFrame = null;
        return;
      }

      this.peekHintFrame = requestAnimationFrame(tick);
    };

    this.peekHintFrame = requestAnimationFrame(tick);
  }

  destroy() {
    this.cancelPeekHint();
    this.root.closest('.card__media')?.classList.remove('card__media--not-first-image');
    if (this.peekHintTimer) clearTimeout(this.peekHintTimer);
    this.root.removeEventListener('pointerdown', this.onPointerDown);
    this.root.removeEventListener('touchmove', this.onTouchMove);
    this.root.removeEventListener('click', this.onClick, true);
    this.root.removeEventListener('dragstart', this.onDragStart);
    this.unbindDragListeners();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    } else {
      window.removeEventListener('resize', this.onResize);
    }
    delete this.root.dataset.cardImageSliderInit;
  }
}

const sliderInstances = new WeakMap();

function initCardImageSliders(root = document) {
  if (!isMobileViewport()) return;

  const scope = root instanceof Element ? root : document;
  scope.querySelectorAll('[data-card-image-slider]:not([data-card-image-slider-init])').forEach((el) => {
    const slides = el.querySelectorAll('[data-card-image-slider-slide]');
    if (slides.length < 2) return;
    const instance = new CardImageSlider(el);
    if (instance.track) {
      sliderInstances.set(el, instance);
    }
  });
}

function destroyCardImageSliders(root = document) {
  const scope = root instanceof Element ? root : document;
  scope.querySelectorAll('[data-card-image-slider][data-card-image-slider-init]').forEach((el) => {
    const instance = sliderInstances.get(el);
    if (instance) {
      instance.destroy();
      sliderInstances.delete(el);
    }
  });
}

window.initCardImageSliders = initCardImageSliders;
window.destroyCardImageSliders = destroyCardImageSliders;

const handleViewportChange = () => {
  if (isMobileViewport()) {
    initCardImageSliders();
  } else {
    destroyCardImageSliders();
  }
};

document.addEventListener('DOMContentLoaded', () => {
  handleViewportChange();
  if (window.matchMedia) {
    window.matchMedia(MOBILE_MEDIA_QUERY).addEventListener('change', handleViewportChange);
  }
});

document.addEventListener('shopify:section:load', (e) => {
  if (isMobileViewport()) {
    initCardImageSliders(e.target);
  }
});
